import { useMemo } from 'react';

import { buildReportSummaries } from './approvalQueueModel';
import { buildBlackoutQueueItem } from './tabs/blackoutDates/blackoutQueue';
import type { BlackoutCoverageSummary } from './tabs/blackoutDates/types';
import { buildBundledExperienceQueueItem } from './tabs/bundledExperiences/bundledExperiencesQueue';
import { buildCampaignQueueItem } from './tabs/campaign/campaignQueue';
import { buildDealOptionsInclusionsQueueItem } from './tabs/dealOptionsInclusions/dealOptionsInclusionsQueue';
import { buildDiscountsMarginQueueItem } from './tabs/discountsMargin/discountsMarginQueue';
import { buildCapacityQueueItem, buildMarginQueueItem } from './tabs/egs/egsQueue';
import { buildSetupQueueItem } from './tabs/setup/setupQueue';
import { buildSurchargeTableQueueItem, buildSurchargesQueueItem } from './tabs/surcharges/surchargesQueue';
import { buildTaxesPaymentsQueueItem } from './tabs/taxesPayments/taxesPaymentsQueue';
import type { ChecklistRowsByCategory, ManualOverrideState } from './types';
import type { VisibleChecklistRows } from './useVisibleChecklistRows';

interface Input {
  blackoutCoverage: BlackoutCoverageSummary;
  manualOverrides: ManualOverrideState;
  offerId: string;
  propertyId?: string;
  rows: ChecklistRowsByCategory;
  vendorId?: string;
  visibleRows: VisibleChecklistRows;
}

export function useApprovalQueueModel({
  blackoutCoverage,
  manualOverrides,
  offerId,
  propertyId,
  rows,
  vendorId,
  visibleRows,
}: Input) {
  const visibleQueueItems = useMemo(
    () => [
      ...visibleRows.visibleSetupRows.map((row) => buildSetupQueueItem(row)),
      ...visibleRows.visibleCampaignRows.map((row) => buildCampaignQueueItem(row)),
      ...visibleRows.visibleDiscountsMarginRows.map((row) => buildDiscountsMarginQueueItem(row)),
      ...visibleRows.visibleBlackoutRows.map((row) => buildBlackoutQueueItem(row, blackoutCoverage)),
      ...visibleRows.visibleMarginRows.map((row) => buildMarginQueueItem(row)),
      ...visibleRows.visibleCapacityRows.map((row) => buildCapacityQueueItem(row)),
      ...visibleRows.visibleDealOptionsInclusionsRows.map((row) => buildDealOptionsInclusionsQueueItem(row)),
      ...visibleRows.visibleSurchargeTableRows.map((row) => buildSurchargeTableQueueItem(row)),
      ...visibleRows.visibleSurchargeRules.map((row) => buildSurchargesQueueItem(row)),
      ...visibleRows.visibleTaxesPaymentsRows.map((row) => buildTaxesPaymentsQueueItem(row)),
      ...visibleRows.visibleBundledExperienceRows.map((row) => buildBundledExperienceQueueItem(row)),
    ],
    [blackoutCoverage, visibleRows],
  );
  const reportSummaries = useMemo(
    () =>
      buildReportSummaries({
        blackoutRows: rows.blackoutRows,
        bundledExperienceRows: rows.bundledExperienceRows,
        baseSurchargesRows: rows.baseSurchargesRows,
        taxesPaymentsRows: rows.taxesPaymentsRows,
        capacityRows: rows.capacityRows,
        campaignRows: rows.campaignRows,
        discountsMarginRows: rows.discountsMarginRows,
        dealOptionsInclusionsRows: rows.dealOptionsInclusionsRows,
        manualOverrides,
        marginRows: rows.marginRows,
        offerId,
        propertyId,
        setupRows: rows.setupRows,
        surchargeTableRows: rows.surchargeTableRows,
        vendorId,
      }),
    [manualOverrides, offerId, propertyId, rows, vendorId],
  );
  return {
    reportSummaries,
    visibleQueueItems,
  };
}
